import apiService from './apiService';

export interface SlackIntegration {
  id: string;
  teamId: string;
  teamName: string | null;
  connected: boolean;
  createdAt: string;
  updatedAt: string;
}

export const slackService = {
  /**
   * Get OAuth URL for Slack connection
   */
  async getOAuthUrl(source?: string): Promise<{ url: string; redirectUri: string }> {
    const params = source ? { source } : undefined;
    return apiService.get('/slack/oauth-url', { params });
  },

  /**
   * Get user's Slack integrations
   */
  async getIntegrations(): Promise<SlackIntegration[]> {
    return apiService.get('/slack/integrations');
  },

  /**
   * Connect a Slack workspace
   */
  async connectSlack(code: string, redirectUri: string): Promise<SlackIntegration> {
    return apiService.post('/slack/connect', {
      code,
      redirectUri,
    });
  },

  /**
   * Disconnect a Slack integration
   */
  async disconnectSlack(integrationId: string): Promise<void> {
    return apiService.delete(`/slack/integrations/${integrationId}`);
  },
};

// LocalStorage key for OAuth communication (bypasses COOP restrictions)
const OAUTH_RESULT_KEY = 'slack_oauth_result';

/**
 * Helper function to initiate Slack OAuth flow
 * Uses popup flow for better UX
 * Uses localStorage for cross-origin communication (COOP workaround)
 */
export async function connectSlackProvider(userId: string): Promise<boolean> {
         try {
           // Clear any previous OAuth result
           localStorage.removeItem(OAUTH_RESULT_KEY);

           // Get OAuth URL from backend
           const { url, redirectUri } = await slackService.getOAuthUrl('web');
           console.log('[SlackService] OAuth URL generated:', { 
             url: url.substring(0, 100) + '...', 
             redirectUri 
           });

           // Open the authorization URL in a popup
           console.log('[SlackService] Opening OAuth popup...');
           const popup = window.open(
             url,
             'Slack Authorization',
             'width=600,height=700'
           );

           if (!popup) {
             console.error('[SlackService] Popup blocked. Please allow popups for this site.');
             return false;
           }
           
           console.log('[SlackService] Popup opened, waiting for OAuth callback...');

    // Create a promise that resolves when OAuth completes
    return new Promise<boolean>((resolve) => {
      let resolved = false;
      let checkInterval: ReturnType<typeof setInterval> | null = null;

      const cleanup = () => {
        if (checkInterval) {
          clearInterval(checkInterval);
          checkInterval = null;
        }
        window.removeEventListener('message', messageHandler);
        window.removeEventListener('storage', storageHandler);
      };

      const resolveOnce = (value: boolean) => {
        if (!resolved) {
          resolved = true;
          cleanup();
          localStorage.removeItem(OAUTH_RESULT_KEY);
          resolve(value);
        }
      };

      // Listen for message from OAuth callback (works if COOP doesn't block)
      const messageHandler = (event: MessageEvent) => {
        if (event.origin !== window.location.origin) {
          return;
        }

        if (event.data.type === 'slack-oauth-callback') {
          const { code, error } = event.data;

          if (error) {
            console.error('OAuth error:', error);
            resolveOnce(false);
            return;
          }

          if (code) {
            handleOAuthCode(code, redirectUri).then((success) => {
              resolveOnce(success);
            });
          } else {
            resolveOnce(false);
          }
        }
      };

      // Listen for localStorage changes (COOP-safe fallback)
      const storageHandler = (event: StorageEvent) => {
        if (event.key === OAUTH_RESULT_KEY && event.newValue) {
          try {
            const result = JSON.parse(event.newValue);
            if (result.type === 'slack-oauth-callback') {
              if (result.success) {
                resolveOnce(true);
              } else if (result.error) {
                console.error('OAuth error from storage:', result.error);
                resolveOnce(false);
              }
            }
          } catch {
            // Invalid JSON, ignore
          }
        }
      };

      window.addEventListener('message', messageHandler);
      window.addEventListener('storage', storageHandler);

      // Poll for localStorage changes and check integration status
      // NOTE: We don't check popup.closed because Slack OAuth uses COOP which blocks it
      let pollCount = 0;
      const maxPolls = 300; // 5 minutes at 1 second intervals
      let integrationCheckScheduled = false;

      checkInterval = setInterval(() => {
        pollCount++;

        // Check localStorage for result (primary method - works with COOP)
        const storedResult = localStorage.getItem(OAUTH_RESULT_KEY);
        if (storedResult) {
          try {
            const result = JSON.parse(storedResult);
            if (result.type === 'slack-oauth-callback') {
              if (result.success) {
                resolveOnce(true);
                return;
              } else if (result.error) {
                console.error('OAuth error from storage:', result.error);
                resolveOnce(false);
                return;
              }
            }
          } catch {
            // Invalid JSON, continue polling
          }
        }

        // After 15 seconds, start checking integration status periodically
        // This handles cases where localStorage communication fails
        if (pollCount >= 15 && pollCount % 5 === 0 && !integrationCheckScheduled) {
          integrationCheckScheduled = true;
          slackService.getIntegrations().then((integrations) => {
            integrationCheckScheduled = false;
            if (integrations && integrations.length > 0) {
              resolveOnce(true);
            }
          }).catch(() => {
            integrationCheckScheduled = false;
          });
        }

        // Timeout after max polls
        if (pollCount >= maxPolls) {
          slackService.getIntegrations().then((integrations) => {
            resolveOnce(integrations && integrations.length > 0);
          }).catch(() => {
            resolveOnce(false);
          });
        }
      }, 1000);

    });
  } catch (error) {
    console.error('Error connecting Slack:', error);
    return false;
  }
}

       /**
        * Handle OAuth code and complete connection
        */
       async function handleOAuthCode(code: string, redirectUri: string): Promise<boolean> {
         try {
           const result = await slackService.connectSlack(code, redirectUri);
           console.log('[SlackService] Connection completed:', result);
           return true;
         } catch (error: any) {
           console.error('[SlackService] Error completing Slack connection:', {
             message: error.message,
             response: error.response?.data,
             status: error.response?.status,
           });
           return false;
         }
       }
